import Title from '@/components/ui/Title';
import type { WorkspaceSocialMediaLink, WorkspaceVisualIdentity } from '@/features/workspace/types';
import { cn } from '@/lib/utils';
import EvaluationFormPreview from '../../thankyou/EvaluationFormPreview';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import OfferThankYou from '@/components/pages/thankyou/OfferThankYou';
import { useEffect, useMemo, useState } from 'react';
import { Separator } from '@/components/ui/separator';
import { socialMediaOptions } from '@/components/pages/webinars/resources/social-media/SocialMediaSection';
import { listPresenters } from '@/features/webinar/webinarThunks';
import { useTranslation } from 'react-i18next';
import { PoweredByLogo } from '@/components/pages/public/PoweredByLogo';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Monitor, Smartphone } from 'lucide-react';
import { toWebinarLng } from '@/lib/webinarLanguage';

type PreviewDevice = 'desktop' | 'mobile';

export default function PreviewThankyouPage({
  visualIdentity,
  displayLogo,
  displayEvaluation,
  displayImage,
  imageUrl,
  displayOffer,
  displaySocialMedia,
  displaySpeakerProfile,
  title,
  subtitle,
  evaluationIntroduction,
  previewOfferId,
}: {
  visualIdentity: WorkspaceVisualIdentity | null;
  displayLogo?: boolean;
  displayEvaluation?: boolean;
  displayImage?: boolean;
  imageUrl?: string | null;
  displayOffer?: boolean;
  displaySocialMedia?: boolean;
  displaySpeakerProfile?: boolean;
  title?: string | null;
  subtitle?: string | null;
  evaluationIntroduction?: string | null;
  previewOfferId?: number | null;
}) {
  const { t } = useTranslation('createWebinar');
  const dispatch = useAppDispatch();
  const { evaluationTemplate, offerTemplates, currentWebinar } = useAppSelector(
    (state: RootState) => state.webinar,
  );
  const { t: tPreview } = useTranslation('webinars', {
    lng: toWebinarLng(currentWebinar?.language),
  });

  const user = useAppSelector((state: RootState) => state.auth.user);
  const workspaceId = useAppSelector((state: RootState) => state.workspace.currentWorkspace?.id);
  const presenters = useAppSelector((state: RootState) => state.webinar.presenters);
  const socialMediaLinks = useAppSelector(
    (state: RootState) => state.workspace.currentWorkspace?.socials ?? [],
  );

  useEffect(() => {
    if (displaySpeakerProfile && currentWebinar?.id && workspaceId) {
      dispatch(listPresenters({ workspaceId, webinarId: currentWebinar.id }));
    }
  }, [displaySpeakerProfile, currentWebinar?.id, workspaceId, dispatch]);

  const thankYouOffer = useMemo(() => {
    if (previewOfferId != null) {
      return offerTemplates.find((offer) => offer.id === previewOfferId) || null;
    }
    return offerTemplates.find((offer) => offer.thankYouPageOffer === true) || null;
  }, [offerTemplates, previewOfferId]);

  const adminName = useMemo(() => {
    if (!user?.firstName && !user?.lastName) return '';
    return `${user.firstName || ''} ${user.lastName || ''}`.trim();
  }, [user?.firstName, user?.lastName]);

  const [device, setDevice] = useState<PreviewDevice>('desktop');
  const isMobile = device === 'mobile';

  const hasOffer = Boolean(displayOffer && thankYouOffer && visualIdentity);
  const hasEvaluation = Boolean(displayEvaluation && evaluationTemplate && visualIdentity);
  const useTwoColumnLayout = hasOffer && hasEvaluation;

  const logoBlock = visualIdentity?.logoUrl && displayLogo && (
    <img src={visualIdentity.logoUrl} alt="logo" className="w-20" />
  );

  const titleBlock = (
    <div className={cn(!useTwoColumnLayout && 'text-center')}>
      <p
        className={cn(
          visualIdentity?.theme === 'DARK' ? 'text-secondary-inverse' : 'text-secondary',
          'text-secondary font-normal',
        )}
      >
        {subtitle?.trim() || t('postEventSection.texts.pageSubtitlePlaceholder')}
      </p>
      <Title
        level={1}
        className={cn(visualIdentity?.theme === 'DARK' ? 'text-white' : 'text-black')}
      >
        {title?.trim() || t('postEventSection.texts.pageTitlePlaceholder')}
      </Title>
    </div>
  );

  const offerBlock = displayOffer && thankYouOffer && visualIdentity && (
    <div className="w-full">
      <OfferThankYou visualIdentity={visualIdentity} offer={thankYouOffer} />
    </div>
  );

  const evaluationBlock = displayEvaluation && evaluationTemplate && visualIdentity && (
    <>
      <p
        className={cn(
          visualIdentity?.theme === 'DARK' ? 'text-secondary-inverse' : 'text-secondary',
          'text-sm whitespace-pre-line',
        )}
      >
        {evaluationIntroduction?.trim() || t('postEventSection.texts.evaluationIntroPlaceholder')}
      </p>
      <EvaluationFormPreview visualIdentity={visualIdentity} evaluation={evaluationTemplate} />
    </>
  );

  const imageBlock = displayImage && imageUrl && (
    <img
      src={imageUrl}
      alt={tPreview('thankyouPage.imageAlt')}
      className="w-full h-full rounded-[20px] object-contain"
    />
  );

  const presenterCardBlock = (displaySpeakerProfile || displaySocialMedia) && (
    <div
      className={cn(
        'w-full p-4 rounded-[20px] flex justify-center gap-4',
        isMobile ? 'flex-col items-center' : 'flex-row items-stretch',
      )}
      style={{
        backgroundColor:
          visualIdentity?.theme === 'DARK' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(31, 31, 31, 0.03)',
      }}
    >
      {displaySpeakerProfile && (
        <div className={cn('flex flex-col gap-3', displaySocialMedia ? 'flex-1' : 'items-center')}>
          {presenters.length > 0 ? (
            presenters.map((presenter) => {
              const displayName = presenter.overrideFullName || presenter.displayName;
              const pictureUrl = presenter.userPictureUrl || '/assets/avatar.svg';

              return (
                <div key={presenter.id} className="flex items-center gap-2">
                  <img
                    src={pictureUrl}
                    alt={displayName}
                    className="size-12 rounded-full object-cover"
                  />
                  <div className="flex flex-col gap-1 items-start justify-center">
                    <p
                      className="text-xl font-medium"
                      style={{
                        color: visualIdentity?.theme === 'DARK' ? 'white' : 'black',
                      }}
                    >
                      {displayName}
                    </p>
                    {presenter.jobTitle && (
                      <p
                        className="text-base break-all"
                        style={{
                          color: visualIdentity?.theme === 'DARK' ? 'white' : 'black',
                        }}
                      >
                        {presenter.jobTitle}
                      </p>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="flex items-center gap-2">
              <img src={user?.pictureUrl ?? ''} alt="admin" className="size-12 rounded-full" />
              <div>
                <p
                  className="text-xl font-medium"
                  style={{ color: visualIdentity?.theme === 'DARK' ? 'white' : 'black' }}
                >
                  {adminName}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
      {displaySpeakerProfile &&
        displaySocialMedia &&
        (isMobile ? (
          <div className="items-stretch flex w-full">
            <Separator orientation="horizontal" className="bg-input w-full h-px" />
          </div>
        ) : (
          <div className="items-stretch flex">
            <Separator orientation="vertical" className="bg-input w-px" />
          </div>
        ))}
      {displaySocialMedia && (
        <div className="space-y-1 flex-1 flex flex-col justify-center items-center gap-2">
          <p
            className="text-sm"
            style={{ color: visualIdentity?.theme === 'DARK' ? 'white' : 'black' }}
          >
            {tPreview('thankyouPage.findMeOnSocial')}
          </p>
          {socialMediaLinks && socialMediaLinks.length > 0 ? (
            <div className="flex items-center gap-2 justify-center">
              {socialMediaLinks.map((link: WorkspaceSocialMediaLink) => {
                const value = link.socialMedia.toUpperCase();
                const option = socialMediaOptions.find((opt) => opt.value === value);

                if (!option?.icon) {
                  return null;
                }

                return (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center"
                  >
                    {option.icon}
                  </a>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center gap-2 justify-center">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index + 'empty-social-media'} className="w-6 h-6 bg-input rounded-full" />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const poweredByLogo = (
    <PoweredByLogo
      theme={visualIdentity?.theme as 'LIGHT' | 'DARK'}
      fontFamily={visualIdentity?.fontFamily ?? undefined}
      isNotLivePage
    />
  );
  const poweredByCentered = (
    <div className="w-full py-4 flex items-center justify-center">{poweredByLogo}</div>
  );

  return (
    <div className="w-full space-y-4">
      <Tabs value={device} onValueChange={(value) => setDevice(value as PreviewDevice)}>
        <TabsList>
          <TabsTrigger value="desktop" className="gap-2">
            <Monitor className="size-4" />
            {t('postEventSection.preview.desktop', { defaultValue: 'Desktop' })}
          </TabsTrigger>
          <TabsTrigger value="mobile" className="gap-2">
            <Smartphone className="size-4" />
            {t('postEventSection.preview.mobile', { defaultValue: 'Mobile' })}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <div
        className={cn(
          'rounded-[20px] relative flex items-center justify-center mx-auto',
          isMobile ? 'w-[390px] p-4' : 'w-full p-8',
        )}
        style={{
          backgroundColor: visualIdentity?.backgroundColor || '#FBFBFB',
        }}
      >
        <div
          className={cn(
            'w-full h-full max-w-[1440px] mx-auto flex flex-col gap-12',
            useTwoColumnLayout
              ? 'lg:flex-row items-start lg:gap-41 justify-between'
              : 'items-center justify-center',
          )}
        >
          {useTwoColumnLayout ? (
            <>
              <div className="w-full flex flex-1 flex-col gap-8">
                {logoBlock}
                {isMobile && offerBlock}
                {titleBlock}
                {evaluationBlock}
              </div>
              <div className="w-full flex flex-1 flex-col gap-6 items-center">
                {!isMobile && (
                  <div className="w-full flex items-center justify-end">{poweredByLogo}</div>
                )}
                {!isMobile && offerBlock}
                {imageBlock}
                {presenterCardBlock}
                {isMobile && poweredByCentered}
              </div>
            </>
          ) : (
            <div className="w-full flex flex-col items-center gap-8 max-w-2xl">
              {logoBlock}
              {titleBlock}
              {offerBlock}
              {evaluationBlock}
              {imageBlock}
              {presenterCardBlock}
              {poweredByCentered}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
